import React, { useState, useRef, useEffect, useMemo, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import ptLabels from 'react-phone-number-input/locale/pt.json';
import enLabels from 'react-phone-number-input/locale/en.json';
import { getAllCountries, getPreferredCountries } from '../../utils/phone';

/**
 * CountrySelectField — seletor de PAÍS DE ENDEREÇO com busca.
 *
 * Substitui o <select> nativo (lista de ~240 países sem busca, inviável no
 * mobile) por um controle com busca. Mesmo tratamento do seletor do telefone,
 * mas é um form control autônomo (não é o countrySelectComponent da lib):
 *  - valor = ISO 3166-1 alpha-2 (ex.: "BR"); onChange(iso) recebe a string.
 *  - popover em portal (não é clipado por modais/overflow).
 *  - busca accent-insensitive por nome (localizado + inglês), ISO e DDI.
 *  - países preferidos da região fixados no topo.
 *  - bandeira via emoji (mesma fonte de dados do getAllCountries()).
 */

const normalize = (s) =>
  (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

const CountrySelectField = ({ value, onChange, placeholder, id, name, disabled, className, style }) => {
  const { t, i18n } = useTranslation();
  const labels = (i18n.language || '').toLowerCase().startsWith('pt') ? ptLabels : enLabels;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);
  const popRef = useRef(null);
  const inputRef = useRef(null);

  // Lista base com nome localizado (o inglês fica guardado para a busca casar os dois).
  const all = useMemo(
    () =>
      getAllCountries()
        .map((c) => ({
          iso: c.country,
          name: labels[c.country] || c.name,
          enName: c.name,
          flag: c.flag,
          code: c.code,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [labels]
  );

  const preferred = useMemo(() => getPreferredCountries(), []);
  const selected = useMemo(() => all.find((c) => c.iso === value), [all, value]);

  const reposition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const width = Math.min(Math.max(r.width, 260), vw - 16);
    let left = r.left;
    if (left + width > vw - 8) left = Math.max(8, vw - 8 - width);
    setCoords({ top: Math.round(r.bottom + 4), left: Math.round(left), width: Math.round(width) });
  }, []);

  useLayoutEffect(() => { if (open) reposition(); }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const onDocDown = (e) => {
      if (popRef.current?.contains(e.target) || triggerRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onMove = () => reposition();
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const idT = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(idT);
  }, [open]);

  const list = useMemo(() => {
    const q = normalize(query);
    if (q) {
      const digits = q.replace(/\D/g, '');
      return all.filter((c) => {
        const name = normalize(c.name);
        const en = normalize(c.enName);
        const iso = c.iso.toLowerCase();
        return name.includes(q) || en.includes(q) || iso === q || (digits && String(c.code).startsWith(digits));
      });
    }
    const prefSet = new Set(preferred);
    const top = preferred.map((iso) => all.find((c) => c.iso === iso)).filter(Boolean);
    const rest = all.filter((c) => !prefSet.has(c.iso));
    return [...top, ...rest];
  }, [all, query, preferred]);

  const select = useCallback((iso) => {
    onChange(iso);
    setOpen(false);
    setQuery('');
  }, [onChange]);

  return (
    <>
      <Trigger
        ref={triggerRef}
        type="button"
        id={id}
        data-name={name}
        className={className}
        style={style}
        disabled={disabled}
        $placeholder={!selected}
        onClick={() => !disabled && setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <TriggerLabel>
          {selected
            ? `${selected.flag} ${selected.name} (+${selected.code})`
            : (placeholder || t('phoneInput.searchPlaceholder', 'Buscar país…'))}
        </TriggerLabel>
        <Arrow $open={open} />
      </Trigger>

      {open && coords && createPortal(
        <Popover ref={popRef} style={{ top: coords.top, left: coords.left, width: coords.width }} role="listbox">
          <SearchWrap>
            <SearchInput
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('phoneInput.searchPlaceholder', 'Buscar país…')}
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
          </SearchWrap>
          <List>
            {list.length === 0 ? (
              <Empty>{t('phoneInput.noResults', 'Nenhum país encontrado')}</Empty>
            ) : (
              list.map((c) => (
                <Item
                  key={c.iso}
                  type="button"
                  $sel={c.iso === value}
                  onClick={() => select(c.iso)}
                >
                  <ItemFlag>{c.flag}</ItemFlag>
                  <ItemName>{c.name}</ItemName>
                  <ItemCode>+{c.code}</ItemCode>
                </Item>
              ))
            )}
          </List>
        </Popover>,
        document.body
      )}
    </>
  );
};

// ===== styled =====

const Trigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  min-height: 48px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 10px;
  font-size: 0.9375rem;
  color: ${(p) => (p.$placeholder ? '#94a3b8' : '#1e293b')};
  cursor: pointer;
  text-align: left;
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:hover:not(:disabled) { border-color: rgba(49, 140, 213, 0.5); }
  &:focus-visible {
    outline: none;
    border-color: rgb(49, 140, 213);
    box-shadow: 0 0 0 3px rgba(49, 140, 213, 0.12);
  }
  &:disabled { background: #f8fafc; color: #94a3b8; cursor: not-allowed; }
`;

const TriggerLabel = styled.span`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Arrow = styled.span`
  display: block;
  width: 7px; height: 7px;
  border: solid #64748b;
  border-width: 0 1.5px 1.5px 0;
  transform: rotate(${(p) => (p.$open ? '-135deg' : '45deg')});
  margin-top: ${(p) => (p.$open ? '3px' : '-3px')};
  transition: transform 0.15s ease;
  flex-shrink: 0;
`;

const Popover = styled.div`
  position: fixed;
  z-index: 3000;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.16);
  max-height: 45vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SearchWrap = styled.div`
  padding: 8px;
  border-bottom: 1px solid #eef2f6;
  background: #fff;
  flex-shrink: 0;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  font-size: 15px;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: #1095F3; box-shadow: 0 0 0 3px rgba(16, 149, 243, 0.12); }
`;

const List = styled.div`
  overflow-y: auto;
  padding: 4px;
  -webkit-overflow-scrolling: touch;
`;

const Item = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  min-height: 44px;
  border: none;
  background: ${(p) => (p.$sel ? '#eef6fe' : 'transparent')};
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  font-size: 14px;
  color: #1e293b;
  &:hover { background: #f1f5f9; }
`;

const ItemFlag = styled.span`
  flex-shrink: 0;
  font-size: 20px;
  line-height: 1;
  width: 26px;
  text-align: center;
`;

const ItemName = styled.span`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemCode = styled.span`
  color: #64748b;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
`;

const Empty = styled.div`
  padding: 24px 12px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
`;

export default CountrySelectField;
